import React, { useRef, useCallback, useState } from "react";
import { Box } from "@mui/material";

interface MuiSplitPaneProps {
  top: React.ReactNode;
  bottom: React.ReactNode;
  /**
   * Optional callback when the size changes
   */
  onChange?: (size: number) => void;
  // default size to restore to on double-click (px)
  defaultSize?: number;
  // fraction of container height under which a pane will collapse (0-0.5). e.g. 0.1 = 10%
  collapseThreshold?: number;
  className?: string;
  /**
   * Initial size of the top pane in px. Prefer using `initialTop`/`initialBottom`.
   */
  initialTop?: number;
  /**
   * Initial size of the bottom pane in px. Prefer using `initialTop`/`initialBottom`.
   */
  initialBottom?: number;
  /**
   * Top pane minimum size in px
   */
  topMin?: number;
  /**
   * Top pane maximum size in px
   */
  topMax?: number;
  /**
   * Bottom pane minimum size in px
   */
  bottomMin?: number;
  /**
   * Bottom pane maximum size in px
   */
  bottomMax?: number;
  /**
   * (Deprecated) legacy initial sizes removed — use `initialSize` instead.
   */
}

export const MuiSplitPane: React.FC<MuiSplitPaneProps> = ({
  top,
  bottom,
  onChange,
  defaultSize = 300,
  // reduce default threshold so pane must be smaller before collapsing
  collapseThreshold = 0.06,
  className,
  // per-pane overrides (optional)
  topMin,
  topMax,
  bottomMin,
  bottomMax,
  initialTop,
  initialBottom,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [internalSize, setInternalSize] = useState<number>(
    // Prefer explicit per-pane initial values
    initialTop ?? initialBottom ?? 150
  );
  const lastSizeRef = useRef<number>(internalSize);
  const pointerYRef = useRef<number | null>(null);
  const collapsedTop = internalSize === 0;
  const initializedRef = useRef(false);

  // fullscreen state
  const [topFullscreen, setTopFullscreen] = useState(false);
  const [bottomFullscreen, setBottomFullscreen] = useState(false);
  const prevSizeRef = useRef<number | null>(null);

  // resolve per-pane min/max with sensible fallbacks to legacy props
  // Resolve per-pane min/max with sensible fallbacks
  const resolvedTopMin = topMin ?? 100;
  const resolvedTopMax = topMax ?? Infinity;
  const resolvedBottomMin = bottomMin ?? 100;
  const resolvedBottomMax = bottomMax ?? Infinity;

  const clamp = useCallback(
    (v: number, containerHeight: number) => {
      const effectiveMaxTop = Math.min(
        resolvedTopMax,
        containerHeight - resolvedBottomMin
      );
      const effectiveMinTop = Math.max(
        resolvedTopMin,
        containerHeight - resolvedBottomMax
      );
      const minT = Number.isFinite(effectiveMinTop)
        ? effectiveMinTop
        : resolvedTopMin;
      const maxT = Number.isFinite(effectiveMaxTop)
        ? effectiveMaxTop
        : resolvedTopMax;
      // guard: if inverted bounds, fall back to resolvedTopMin/max
      if (maxT < minT)
        return Math.max(resolvedTopMin, Math.min(resolvedTopMax, v));
      return Math.max(minT, Math.min(maxT, v));
    },
    [resolvedTopMin, resolvedTopMax, resolvedBottomMin, resolvedBottomMax]
  );

  const startDragging = (_initialClientY: number) => {
    if (!containerRef.current) return;
    setIsDragging(true);
    const startRect = containerRef.current.getBoundingClientRect();
    console.debug("[MuiSplitPane] startDragging rect:", {
      top: startRect.top,
      bottom: startRect.bottom,
      height: startRect.height,
    });

    const onMove = (e: MouseEvent | TouchEvent) => {
      if ((e as TouchEvent).touches) e.preventDefault?.();
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const containerHeight = rect.height;
      const y = (e as TouchEvent).touches
        ? (e as TouchEvent).touches[0].clientY
        : (e as MouseEvent).clientY;
      pointerYRef.current = y;

      let newTopHeight = y - rect.top;
      const collapsePxMove =
        Math.max(0, Math.min(0.5, collapseThreshold)) * containerHeight;
      console.debug("[MuiSplitPane] onMove", {
        y,
        rectTop: rect.top,
        rectBottom: rect.bottom,
        containerHeight,
        collapsePxMove,
      });

      // clamp to current container height so dragging down uses fresh metrics
      newTopHeight = Math.max(0, Math.min(containerHeight, newTopHeight));

      // set internal state and notify parent if requested
      setInternalSize(newTopHeight);
      onChange?.(newTopHeight);
      lastSizeRef.current = newTopHeight;
      console.debug("[MuiSplitPane] transient size:", newTopHeight);
    };

    const onUp = () => {
      document.body.style.cursor = "";
      setIsDragging(false);

      const final = lastSizeRef.current;
      const rect = containerRef.current?.getBoundingClientRect();
      const containerHeight = rect ? rect.height : 0;
      const collapsePx =
        Math.max(0, Math.min(0.5, collapseThreshold)) * containerHeight;
      const pointerY = pointerYRef.current;
      console.debug("[MuiSplitPane] onUp", {
        final,
        pointerY,
        rectTop: rect?.top,
        rectBottom: rect?.bottom,
        containerHeight,
        collapsePx,
      });

      // Collapse rule: use the collapse pixel threshold as the trigger.
      // resolvedTopMin is enforced as a sensible fallback.
      const topTooSmall = final <= collapsePx;
      const bottomTooSmall = containerHeight - final <= collapsePx;

      if (topTooSmall) {
        console.debug("[MuiSplitPane] snapping: collapse top (<= collapsePx)", {
          final,
          resolvedTopMin: resolvedTopMin,
          collapsePx,
        });
        setInternalSize(0);
        onChange?.(0);
      } else if (bottomTooSmall) {
        console.debug(
          "[MuiSplitPane] snapping: collapse bottom (<= collapsePx)",
          {
            final,
            containerHeight,
            resolvedBottomMin: resolvedBottomMin,
            collapsePx,
          }
        );
        setInternalSize(containerHeight);
        onChange?.(containerHeight);
      } else if (final > 0 && final < resolvedTopMin) {
        // If user releases between collapse threshold and resolvedTopMin, treat this
        // as an intent to collapse rather than enforcing the minimum size.
        console.debug(
          "[MuiSplitPane] snapping: collapse top because final < resolvedTopMin",
          { final, resolvedTopMin }
        );
        setInternalSize(0);
        onChange?.(0);
      } else {
        // compute effective bounds for the top pane so bottom constraints are respected
        const effectiveMaxTop = Math.min(
          resolvedTopMax,
          containerHeight - resolvedBottomMin
        );
        const effectiveMinTop = Math.max(
          resolvedTopMin,
          containerHeight - resolvedBottomMax
        );
        const clamped = clamp(final, containerHeight);
        console.debug("[MuiSplitPane] committing final size", {
          final,
          clamped,
          effectiveMinTop,
          effectiveMaxTop,
        });
        setInternalSize(clamped);
        onChange?.(clamped);
      }

      pointerYRef.current = null;

      // allow layout-sensitive children (Monaco editor, tables) to reflow
      // after collapse/restore by dispatching a resize event
      setTimeout(() => window.dispatchEvent(new Event("resize")), 60);

      window.removeEventListener("mousemove", onMove as EventListener);
      window.removeEventListener("touchmove", onMove as EventListener);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchend", onUp);
    };

    document.body.style.cursor = "row-resize";
    window.addEventListener("mousemove", onMove as EventListener);
    window.addEventListener(
      "touchmove",
      onMove as EventListener,
      { passive: false } as AddEventListenerOptions
    );
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchend", onUp);
  };

  // On first mount, if the parent didn't provide a positive `size`, use `initialSize`.
  React.useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    const useSize = initialTop ?? initialBottom ?? resolvedTopMin;
    setInternalSize(useSize);
    lastSizeRef.current = useSize;
    onChange?.(useSize);
    // we intentionally want to run only once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    startDragging(e.clientY);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    if (e.touches && e.touches[0]) startDragging(e.touches[0].clientY);
  };

  // Fullscreen toggle helpers
  const toggleTopFullscreen = () => {
    if (!containerRef.current) return;
    const h = containerRef.current.getBoundingClientRect().height;
    if (!topFullscreen) {
      prevSizeRef.current = internalSize;
      setInternalSize(h);
      onChange?.(h);
      setTopFullscreen(true);
      setBottomFullscreen(false);
    } else {
      const restore =
        prevSizeRef.current ?? initialTop ?? initialBottom ?? resolvedTopMin;
      setInternalSize(restore);
      onChange?.(restore);
      prevSizeRef.current = null;
      setTopFullscreen(false);
    }
    setTimeout(() => window.dispatchEvent(new Event("resize")), 50);
  };

  const toggleBottomFullscreen = () => {
    if (!containerRef.current) return;
    if (!bottomFullscreen) {
      prevSizeRef.current = internalSize;
      // Hide the top pane so the bottom pane fills the container.
      setInternalSize(0);
      onChange?.(0);
      setBottomFullscreen(true);
      setTopFullscreen(false);
    } else {
      const restore =
        prevSizeRef.current ?? initialTop ?? initialBottom ?? resolvedTopMin;
      setInternalSize(restore);
      onChange?.(restore);
      prevSizeRef.current = null;
      setBottomFullscreen(false);
    }
    setTimeout(() => window.dispatchEvent(new Event("resize")), 50);
  };

  return (
    <Box
      ref={containerRef}
      className={className}
      sx={{
        height: "100%",
        width: "100%",
        position: "relative",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          height: `${internalSize}px`,
          minHeight: isDragging || collapsedTop ? 0 : resolvedTopMin,
          overflow: "hidden",
          width: "100%",
          position: "relative",
        }}
      >
        {top}

        {/* Top fullscreen button (managed by split pane) */}
        <button
          onClick={toggleTopFullscreen}
          title={topFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            padding: "6px 12px",
            fontSize: 12,
            cursor: "pointer",
            border: "1px solid rgba(0,0,0,0.12)",
            borderRadius: 4,
            background: "rgba(255,255,255,0.9)",
            zIndex: 30,
          }}
        >
          {topFullscreen ? "Exit Fullscreen" : "Fullscreen"}
        </button>
      </Box>

      <Box
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onDoubleClick={() => {
          setInternalSize(defaultSize);
          onChange?.(defaultSize);
        }}
        sx={{
          height: "10px",
          cursor: "row-resize",
          background: "transparent",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          userSelect: "none",
          WebkitUserSelect: "none",
        }}
        role="separator"
        aria-orientation="horizontal"
      >
        <Box
          sx={{
            width: 56,
            height: 8,
            borderRadius: 4,
            position: "relative",
            background: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.22)"
                : "rgba(0,0,0,0.06)",
            boxShadow: (theme) =>
              theme.palette.mode === "dark"
                ? "0 1px 4px rgba(0,0,0,0.6)"
                : "0 1px 2px rgba(0,0,0,0.06)",
            border: (theme) =>
              theme.palette.mode === "dark"
                ? "1px solid rgba(255,255,255,0.16)"
                : "1px solid rgba(0,0,0,0.06)",
            "&:hover": {
              background: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.32)"
                  : "rgba(0,0,0,0.08)",
            },
            zIndex: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0",
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 4,
              borderRadius: 2,
              backgroundImage: (theme) => {
                const gripColor =
                  theme.palette.mode === "dark"
                    ? "rgba(0,0,0,0.45)"
                    : "rgba(255,255,255,0.65)";
                return `radial-gradient(circle, ${gripColor} 25%, transparent 26%)`;
              },
              backgroundSize: "6px 6px",
              backgroundPosition: "0 50%",
              backgroundRepeat: "repeat-x",
            }}
          />
        </Box>
      </Box>

      <Box
        sx={{ flex: 1, overflow: "auto", width: "100%", position: "relative" }}
      >
        {bottom}

        {/* Bottom fullscreen button - shows when not collapsed */}
        <button
          onClick={toggleBottomFullscreen}
          title={bottomFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            padding: "6px 12px",
            fontSize: 12,
            cursor: "pointer",
            border: "1px solid rgba(0,0,0,0.12)",
            borderRadius: 4,
            background: "rgba(255,255,255,0.9)",
            zIndex: 30,
          }}
        >
          {bottomFullscreen ? "Exit Fullscreen" : "Fullscreen"}
        </button>
      </Box>
    </Box>
  );
};

export default MuiSplitPane;
